import { useTranslation } from 'react-i18next';
import { fetchTemplateCoverBlob } from '@kidshome/api-contract';
import { cn } from '@kidshome/ui';
import { choreGlyph } from '@/shared/ui/glyphs';
import { choreArt } from '@/assets/icons';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { AuthImage } from '@/shared/ui/AuthImage';

/**
 * A chore/mission's image (ADR-0141; mirrors {@link RewardImage}). When the template has an uploaded
 * `coverPhotoKey` it renders the guardian-curated cover — the book cover, etc. — fetched through the
 * auth-gated, BOLA-checked serve route (`GET /v1/tasks/templates/photo/:key` via {@link AuthImage}),
 * `object-cover`, with a graceful chore-glyph fallback while loading / on error. Otherwise it shows the
 * chore's `icon` glyph/art (via {@link ConceptIcon}). Decorative (the title is rendered beside it), so
 * the glyph path is `aria-hidden`.
 */
export function MissionCover({
  icon,
  coverPhotoKey,
  className,
  rounded = 'rounded-card',
  glyphSize = 32,
}: {
  icon: string;
  /** The opaque uploaded-cover key, or null/undefined to show the icon glyph. */
  coverPhotoKey?: string | null;
  /** Sizing/extra classes for the square (e.g. `size-16`, `size-full`). */
  className?: string;
  /** Corner radius class (a shelf tile is `rounded-card`). */
  rounded?: string;
  /**
   * Glyph render size when there's no cover photo. A shelf tile passes a large value so the icon FILLS
   * the placeholder (rather than floating in empty space) — keeping icon tiles visually as "full" as
   * photo tiles.
   */
  glyphSize?: number;
}): React.ReactElement {
  const { t } = useTranslation('family');

  if (coverPhotoKey) {
    return (
      <span className={cn('inline-flex shrink-0 overflow-hidden', rounded, className)}>
        <AuthImage
          imageKey={coverPhotoKey}
          fetcher={fetchTemplateCoverBlob}
          alt={t('missions.coverAlt')}
          fallbackLabel={t('missions.coverAlt')}
          fallbackGlyph={choreGlyph(icon)}
          className="size-full object-cover"
        />
      </span>
    );
  }

  return (
    <span
      className={cn(
        // A soft brand-tinted gradient (not a flat muted square) + a large icon so the placeholder
        // reads as an intentional cover, keeping icon tiles as "full" as photo tiles on the shelf.
        'inline-flex shrink-0 items-center justify-center bg-gradient-to-br from-primary/5 to-primary/15',
        rounded,
        className,
      )}
      aria-hidden="true"
    >
      <ConceptIcon art={choreArt(icon)} glyph={choreGlyph(icon)} size={glyphSize} />
    </span>
  );
}
